import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Pill, AlertTriangle, Clock, Shield, Brain,
  Activity, Heart, CheckCircle2, Info, Utensils,
  BookOpen, FlaskConical, Stethoscope, Zap, Thermometer
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';

const THYROID_PROTOCOL = [
  {
    name: 'Selenium',
    dosage: '200mcg daily',
    timing: 'With lunch or dinner',
    icon: Shield,
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    evidence: 'A',
    why: "Selenium is critical for thyroid hormone conversion (T4 to active T3). The thyroid contains more selenium per gram than any other organ. Multiple RCTs show selenium reduces thyroid antibodies (TPO-Ab) in Hashimoto's by 40-60%.",
    interactions: ['Safe with Levothyroxine — no timing restrictions', 'May enhance immune checkpoint inhibitors', 'Synergistic with Zinc for thyroid function'],
    notes: 'Brazil nuts are the richest food source (1-2 nuts = ~200mcg). Do NOT exceed 400mcg/day — selenium toxicity is real.'
  },
  {
    name: 'Zinc',
    dosage: '25-30mg daily',
    timing: 'With meals to prevent nausea',
    icon: Zap,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'A',
    why: 'Zinc is required for thyroid hormone synthesis and T4-to-T3 conversion. Deficiency is common in hypothyroid patients and creates a vicious cycle — low thyroid reduces zinc absorption, and low zinc impairs thyroid function.',
    interactions: ['Take 2+ hours apart from Levothyroxine (zinc can bind to it)', 'Separate from iron supplements by 2 hours', 'Long-term use requires copper supplementation (2mg Cu per 30mg Zn)'],
    notes: 'Zinc Picolinate or Zinc Bisglycinate for best absorption. Always take with food to avoid nausea. Balance with copper long-term.'
  },
  {
    name: 'Vitamin D3',
    dosage: '2,000-5,000 IU daily',
    timing: 'Morning with breakfast (fat-containing meal)',
    icon: Heart,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
    evidence: 'A',
    why: "Vitamin D deficiency is found in 60-80% of Hashimoto's patients. D3 modulates the immune system to reduce autoimmune attacks on the thyroid. Studies show supplementation significantly reduces TPO and Tg antibodies.",
    interactions: ['Take 4+ hours apart from Levothyroxine', 'Safe with Methimazole/PTU', 'Enhances calcium absorption — monitor if on calcium supplements'],
    notes: 'Get tested (aim for 50-60 ng/mL). Take with K2 (MK-7) to direct calcium to bones, not arteries.'
  },
  {
    name: 'Iron (if deficient)',
    dosage: '18-36mg elemental iron',
    timing: 'Morning on an empty stomach with Vitamin C',
    icon: Activity,
    color: 'bg-red-100 text-red-700 border-red-200',
    evidence: 'A',
    why: 'Iron is required for thyroid peroxidase (TPO), the enzyme that produces thyroid hormones. Iron deficiency is extremely common in hypothyroid patients (especially women) and directly impairs thyroid function even with medication.',
    interactions: ['Take 4+ hours apart from Levothyroxine (CRITICAL — iron blocks absorption)', 'Separate from calcium, zinc, and coffee by 2+ hours', 'Vitamin C (200mg) taken with iron doubles absorption'],
    notes: 'ONLY supplement if blood tests confirm deficiency (ferritin < 30). Iron Bisglycinate is gentlest on the stomach. Excess iron is toxic.'
  },
  {
    name: 'Vitamin B12 (Methylcobalamin)',
    dosage: '1,000-2,000mcg sublingual',
    timing: 'Morning, under the tongue',
    icon: Brain,
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    evidence: 'B+',
    why: "Up to 40% of hypothyroid patients are B12 deficient. Hashimoto's is associated with pernicious anemia (autoimmune B12 deficiency). Symptoms overlap: fatigue, brain fog, numbness — often attributed to thyroid when actually B12-related.",
    interactions: ['Safe with all thyroid medications', 'Metformin depletes B12', 'Sublingual form bypasses absorption issues common in autoimmune conditions'],
    notes: 'Methylcobalamin (not cyanocobalamin) for best bioavailability. Get B12 and homocysteine levels tested.'
  },
  {
    name: 'Ashwagandha (KSM-66)',
    dosage: '300-600mg daily',
    timing: 'Morning with breakfast',
    icon: FlaskConical,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    evidence: 'B+',
    why: 'Ashwagandha directly stimulates thyroid hormone production. An 8-week RCT showed significant increases in T3 and T4 levels in subclinical hypothyroid patients. Also reduces cortisol, which suppresses thyroid function.',
    interactions: ['May INCREASE thyroid hormone levels — monitor if on Levothyroxine (dose may need reduction)', 'NOT recommended for hyperthyroid/Graves\' disease', 'Avoid with immunosuppressants'],
    notes: "HYPOTHYROID ONLY — do NOT use if hyperthyroid (Graves'). KSM-66 root extract is the studied form. Monitor TSH after starting."
  },
  {
    name: 'L-Tyrosine',
    dosage: '500-1000mg daily',
    timing: 'Morning on an empty stomach, 30 min before food',
    icon: Zap,
    color: 'bg-sky-100 text-sky-700 border-sky-200',
    evidence: 'B',
    why: 'Tyrosine is the amino acid backbone of thyroid hormones (T3 and T4 literally contain tyrosine). Supplementation provides the raw material for thyroid hormone synthesis. Also supports dopamine production, helping with thyroid-related depression.',
    interactions: ['Take separately from Levothyroxine (2+ hours)', 'Avoid with MAOIs', 'May interact with L-DOPA medications'],
    notes: 'Most effective for subclinical hypothyroidism. N-Acetyl L-Tyrosine (NALT) for better absorption.'
  },
  {
    name: 'Iodine (with extreme caution)',
    dosage: '150-300mcg (NOT milligrams)',
    timing: 'With meals',
    icon: AlertTriangle,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'B-',
    why: "Iodine is essential for thyroid hormone production — T4 contains 4 iodine atoms, T3 contains 3. However, excess iodine can WORSEN Hashimoto's by triggering more autoimmune attacks. This is the most controversial thyroid supplement.",
    interactions: ['Can interfere with Methimazole/PTU effectiveness', 'Excess worsens autoimmune thyroiditis', 'Never combine high-dose iodine with Amiodarone'],
    notes: "TEST FIRST — only supplement if urinary iodine confirms deficiency. Start very low (150mcg). High doses (>500mcg) can trigger thyroid storms in Graves' disease. Kelp supplements often contain dangerously variable amounts."
  }
];

const THYROID_MEDS = [
  { name: 'Levothyroxine (Synthroid, Levoxyl)', notes: 'Take on an EMPTY stomach, 30-60 min before food. Iron, calcium, zinc, and coffee must be separated by 4+ hours. Selenium, B12, and Vitamin D can be taken with meals later in the day.' },
  { name: 'Liothyronine (Cytomel/T3)', notes: 'Same spacing rules as Levothyroxine. Selenium supports T4-to-T3 conversion, potentially allowing lower T3 doses over time. Monitor closely with Ashwagandha.' },
  { name: 'Armour Thyroid / NP Thyroid', notes: 'Natural desiccated thyroid follows same supplement spacing rules. Iron and calcium are the biggest interaction risks. May need dose adjustment if adding Ashwagandha.' },
  { name: 'Methimazole (Tapazole) — Hyperthyroid', notes: 'For Graves\'/hyperthyroid: DO NOT take Ashwagandha, L-Tyrosine, or high-dose Iodine (they increase thyroid hormones). Selenium, Vitamin D, and Zinc are safe and beneficial.' },
  { name: 'PTU (Propylthiouracil) — Hyperthyroid', notes: 'Same cautions as Methimazole. Selenium is particularly important as it helps modulate autoimmune activity in Graves\' disease.' },
];

const LIFESTYLE_TIPS = [
  {
    icon: Utensils,
    title: 'Goitrogen Awareness',
    content: "Raw cruciferous vegetables (broccoli, kale, cauliflower, cabbage) contain goitrogens that can interfere with iodine uptake. COOKING reduces goitrogens by 80-90%. You don't need to avoid them — just cook them. Soy isoflavones can also interfere with thyroid hormone absorption."
  },
  {
    icon: Activity,
    title: 'Exercise for Thyroid',
    content: 'Exercise stimulates thyroid hormone secretion and improves T4-to-T3 conversion. Aim for 30-45 min of moderate exercise 4-5x/week. Mix cardio and strength training. Avoid overtraining — excessive exercise raises cortisol which suppresses thyroid.'
  },
  {
    icon: Brain,
    title: 'Stress & Cortisol Management',
    content: "Chronic stress raises cortisol, which directly suppresses TSH and impairs T4-to-T3 conversion. Prioritize sleep (7-9 hours), practice stress-reduction techniques, and consider adaptogenic herbs. Cortisol management is often the missing piece in thyroid recovery."
  },
  {
    icon: Thermometer,
    title: 'Gut Health Connection',
    content: '20% of T4-to-T3 conversion happens in the gut. Poor gut health = poor thyroid function. Support gut health with probiotics, fermented foods, and L-Glutamine. Test for and treat SIBO, H. pylori, or gluten sensitivity — all linked to thyroid dysfunction.'
  }
];

const EvidenceGrade = ({ grade }) => {
  const colors = {
    'A': 'bg-green-100 text-green-800 border-green-300',
    'B+': 'bg-blue-100 text-blue-800 border-blue-300',
    'B': 'bg-sky-100 text-sky-800 border-sky-300',
    'B-': 'bg-gray-100 text-gray-800 border-gray-300'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${colors[grade] || colors['B']}`}>
      Evidence: {grade}
    </span>
  );
};

export default function ThyroidGuidePage() {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="thyroid-guide-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="thyroid-guide-page">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">Thyroid Support Guide</h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            Evidence-based supplement protocol for hypothyroid (Hashimoto's) and hyperthyroid (Graves') conditions, with medication timing guides.
          </p>
        </motion.div>

        {/* Disclaimer */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8" data-testid="thyroid-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">Important Medical Disclaimer</p>
              <p className="text-xs text-amber-800">
                This guide is for educational purposes only. Always discuss supplements with your endocrinologist, 
                especially regarding timing with thyroid medication. Some supplements can increase or decrease thyroid hormone levels — monitoring is essential.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Hypo vs Hyper callout */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="font-semibold text-blue-900 text-sm mb-1">Hypothyroid / Hashimoto's</p>
            <p className="text-xs text-blue-700">All 8 supplements below are applicable. Ashwagandha and L-Tyrosine are particularly beneficial for boosting low thyroid function.</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <p className="font-semibold text-orange-900 text-sm mb-1">Hyperthyroid / Graves'</p>
            <p className="text-xs text-orange-700">Use Selenium, Zinc, Vitamin D, B12, and Iron (if deficient). AVOID Ashwagandha, L-Tyrosine, and high-dose Iodine — they can worsen hyperthyroidism.</p>
          </div>
        </motion.div>

        {/* Daily Schedule */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-border p-5 mb-8" data-testid="thyroid-schedule">
          <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Medication-Safe Daily Schedule
          </h3>
          <div className="space-y-3">
            {[
              { time: 'Wake up (empty stomach)', supps: 'Levothyroxine FIRST — wait 30-60 min before anything else', color: 'bg-red-50 border-red-200 text-red-900' },
              { time: 'Breakfast (30-60 min after meds)', supps: 'Vitamin D3 + K2, B12 (sublingual), L-Tyrosine (if taken)', color: 'bg-amber-50 border-amber-200 text-amber-900' },
              { time: 'Lunch', supps: 'Selenium, Zinc (with food), Ashwagandha', color: 'bg-sky-50 border-sky-200 text-sky-900' },
              { time: '4+ hours after Levothyroxine', supps: 'Iron (if needed) + Vitamin C — on empty stomach', color: 'bg-orange-50 border-orange-200 text-orange-900' },
              { time: 'Dinner/Evening', supps: 'Iodine (if supplementing, with food)', color: 'bg-indigo-50 border-indigo-200 text-indigo-900' }
            ].map((slot, i) => (
              <div key={i} className={`rounded-xl border p-3 ${slot.color}`}>
                <p className="text-xs font-semibold">{slot.time}</p>
                <p className="text-xs mt-1">{slot.supps}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Supplement Protocol */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            Thyroid Supplement Protocol
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            8 evidence-based supplements. Start with A-rated essentials and add based on your blood work and symptoms.
          </p>

          <Accordion type="multiple" className="space-y-3">
            {THYROID_PROTOCOL.map((supp, index) => {
              const Icon = supp.icon;
              return (
                <motion.div key={supp.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * index }}>
                  <AccordionItem value={supp.name} className="bg-white rounded-xl border border-border overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50/50">
                      <div className="flex items-center gap-3 text-left">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${supp.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-sm text-[#1A1A1A]">{supp.name}</h3>
                            <EvidenceGrade grade={supp.evidence} />
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{supp.dosage}</span>
                            <span>{supp.timing}</span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4 pt-2">
                        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                          <h4 className="text-sm font-medium text-emerald-900 mb-1 flex items-center gap-2">
                            <FlaskConical className="w-4 h-4" />
                            Why This Matters for Thyroid
                          </h4>
                          <p className="text-xs text-emerald-800">{supp.why}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Medication Interactions
                          </h4>
                          <ul className="space-y-1">
                            {supp.interactions.map((int_, i) => (
                              <li key={i} className="text-xs text-blue-800 flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {int_}
                              </li>
                            ))}
                          </ul>
                        </div>
                        {supp.notes && (
                          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                            <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                              <Info className="w-4 h-4" />
                              Clinical Note
                            </h4>
                            <p className="text-xs text-gray-700">{supp.notes}</p>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              );
            })}
          </Accordion>
        </motion.div>

        {/* Medication Compatibility */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            Medication Compatibility
          </h2>
          <p className="text-sm text-muted-foreground mb-6">Supplement timing and safety with common thyroid medications.</p>
          <div className="space-y-3" data-testid="thyroid-med-compatibility">
            {THYROID_MEDS.map((med, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-4">
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">{med.name}</h4>
                <p className="text-xs text-muted-foreground">{med.notes}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Lifestyle */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            Thyroid Lifestyle Strategies
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-testid="thyroid-lifestyle">
            {LIFESTYLE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{tip.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="grid grid-cols-2 gap-3 mb-10">
          <Link to="/quiz" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Pill className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Get Personalized Protocol</p>
            <p className="text-xs text-muted-foreground mt-1">Take the full assessment</p>
          </Link>
          <Link to="/ask" className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
            <Brain className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Ask AI About Thyroid</p>
            <p className="text-xs text-muted-foreground mt-1">Get supplement answers</p>
          </Link>
        </motion.div>
      </main>
      <GlobalFooter />
    </div>
  );
}
